import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  Button,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import { eventSubEventsRegistry } from "../../../../../data-store/actions/events-actions";
import { eventParticipants } from "../../../../../data-store/actions/registry-actions";
import {
  listarModalidades,
  listarCategoriasDeModalidad,
  resolverModalidadLabel,
  resolverCategoriaLabel,
} from "../Configuration/BulkEdit/bulkEditLogic";

// Selector de participantes para armar el PDF de etiquetas — copia del
// bloque de filtro+selección de CampaignEmailContainer.jsx (envío masivo de
// correos), sin la parte de redacción/envío. Se duplica a propósito en vez
// de compartir un componente con CampaignEmailContainer, para no arriesgar
// esa funcionalidad ya en producción.
const NOMBRE_RAMA = { V: "Varonil", F: "Femenil", M: "Mixto" };

// Columnas ordenables de la tabla — "numero" es el orden por defecto porque
// las etiquetas casi siempre se imprimen y se pegan en orden de dorsal.
const COLUMNAS_ORDENABLES = [
  { key: "numero", label: "Folio", tipo: "numero" },
  { key: "__nombreCompleto", label: "Nombre", tipo: "texto" },
  { key: "__modalidadLabel", label: "Modalidad", tipo: "texto" },
  { key: "__categoriaLabel", label: "Categoría", tipo: "texto" },
  { key: "__ramaLabel", label: "Género", tipo: "texto" },
];

function compararFilas(a, b, campo, tipo, direccion) {
  const signo = direccion === "asc" ? 1 : -1;
  if (tipo === "numero") {
    const na = Number(a[campo]);
    const nb = Number(b[campo]);
    const va = Number.isFinite(na) ? na : Infinity;
    const vb = Number.isFinite(nb) ? nb : Infinity;
    return (va - vb) * signo;
  }
  return String(a[campo] || "").localeCompare(String(b[campo] || "")) * signo;
}

export default function ParticipantSelector({ eventoIndex, onSelectionChange }) {
  const dispatch = useDispatch();
  const eventParticipantsState = useSelector((state) => state.registry.eventParticipants);
  const eventSubEventsRegistryState = useSelector((state) => state.events.eventSubEventsRegistry);

  useEffect(() => {
    dispatch(eventSubEventsRegistry(eventoIndex));
    dispatch(eventParticipants(eventoIndex));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoIndex]);

  const [soloCompletos, setSoloCompletos] = useState(true);
  const [textoLibre, setTextoLibre] = useState("");
  const [modalidad, setModalidad] = useState("");
  const [categoriaValor, setCategoriaValor] = useState("");
  const [rama, setRama] = useState("");
  const [selectedKeys, setSelectedKeys] = useState([]);
  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const [ordenarPor, setOrdenarPor] = useState("numero");
  const [direccionOrden, setDireccionOrden] = useState("asc");

  const modalidadOptions = useMemo(
    () => listarModalidades(eventSubEventsRegistryState),
    [eventSubEventsRegistryState]
  );
  const categoriaOptions = useMemo(
    () => listarCategoriasDeModalidad(eventSubEventsRegistryState, modalidad),
    [eventSubEventsRegistryState, modalidad]
  );

  const enrichedRows = useMemo(() => {
    const list = Object.values(eventParticipantsState || {});
    return list.map((r) => ({
      ...r,
      __modalidadLabel: resolverModalidadLabel(eventSubEventsRegistryState, r.modalidad),
      __categoriaLabel: resolverCategoriaLabel(
        eventSubEventsRegistryState,
        r.modalidad,
        r.rama,
        r.categoria
      ),
      __ramaLabel: NOMBRE_RAMA[r.rama] || r.rama || "",
      __nombreCompleto: `${r.nombres || ""} ${r.apellidos || ""}`.trim(),
    }));
  }, [eventParticipantsState, eventSubEventsRegistryState]);

  const filteredRows = useMemo(() => {
    const txt = textoLibre.trim().toLowerCase();
    return enrichedRows.filter((r) => {
      if (soloCompletos && (!r.statusPago || r.statusPago.status !== "ok" || r.borrado)) {
        return false;
      }
      if (modalidad !== "" && String(r.modalidad) !== String(modalidad)) return false;
      if (rama !== "" && r.rama !== rama) return false;
      if (categoriaValor) {
        const [ramaSel, catSel] = categoriaValor.split("|");
        if (String(r.categoria) !== String(catSel) || r.rama !== ramaSel) return false;
      }
      if (txt) {
        const hay = `${r.numero} ${r.nombres} ${r.apellidos}`.toLowerCase();
        if (!hay.includes(txt)) return false;
      }
      return true;
    });
  }, [enrichedRows, soloCompletos, modalidad, rama, categoriaValor, textoLibre]);

  const sortedRows = useMemo(() => {
    const columna = COLUMNAS_ORDENABLES.find((c) => c.key === ordenarPor) || COLUMNAS_ORDENABLES[0];
    return [...filteredRows].sort((a, b) =>
      compararFilas(a, b, columna.key, columna.tipo, direccionOrden)
    );
  }, [filteredRows, ordenarPor, direccionOrden]);

  function ordenarPorColumna(campo) {
    if (ordenarPor === campo) {
      setDireccionOrden((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrdenarPor(campo);
      setDireccionOrden("asc");
    }
  }

  const toggleSelected = useCallback((row) => {
    setSelectedKeys((prev) => {
      const s = new Set(prev);
      if (s.has(row.key)) s.delete(row.key);
      else s.add(row.key);
      return Array.from(s);
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    setSelectedKeys((prev) => {
      const s = new Set(prev);
      filteredRows.forEach((r) => s.add(r.key));
      return Array.from(s);
    });
  }, [filteredRows]);

  const unselectAllVisible = useCallback(() => {
    setSelectedKeys((prev) => {
      const s = new Set(prev);
      filteredRows.forEach((r) => s.delete(r.key));
      return Array.from(s);
    });
  }, [filteredRows]);

  const headerAllChecked =
    filteredRows.length > 0 && filteredRows.every((r) => selectedSet.has(r.key));
  const headerIndeterminate =
    filteredRows.some((r) => selectedSet.has(r.key)) && !headerAllChecked;

  const seleccionados = useMemo(
    () => filteredRows.filter((r) => selectedSet.has(r.key)),
    [filteredRows, selectedSet]
  );

  useEffect(() => {
    if (onSelectionChange) onSelectionChange(seleccionados);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seleccionados]);

  return (
    <Box>
      <Box mb={2} display="flex" style={{ gap: 12 }} flexWrap="wrap" alignItems="center">
        <TextField
          label="Buscar (nombre o folio)"
          value={textoLibre}
          onChange={(e) => setTextoLibre(e.target.value)}
          style={{ minWidth: 220 }}
        />
        <FormControl style={{ minWidth: 200 }}>
          <InputLabel>Modalidad</InputLabel>
          <Select
            value={modalidad}
            onChange={(e) => {
              setModalidad(e.target.value);
              setCategoriaValor("");
            }}
          >
            <MenuItem value="">Todas</MenuItem>
            {modalidadOptions.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl style={{ minWidth: 200 }} disabled={!modalidad}>
          <InputLabel>Categoría</InputLabel>
          <Select value={categoriaValor} onChange={(e) => setCategoriaValor(e.target.value)}>
            <MenuItem value="">Todas</MenuItem>
            {categoriaOptions.map((c) => (
              <MenuItem key={c.rama + "|" + c.value} value={c.rama + "|" + c.value}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl style={{ minWidth: 150 }}>
          <InputLabel>Género</InputLabel>
          <Select value={rama} onChange={(e) => setRama(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="V">Varonil</MenuItem>
            <MenuItem value="F">Femenil</MenuItem>
            <MenuItem value="M">Mixto</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch checked={soloCompletos} onChange={(e) => setSoloCompletos(e.target.checked)} />
          }
          label="Solo inscripciones completas"
        />
      </Box>

      <Box mb={1} display="flex" style={{ gap: 12 }} alignItems="center">
        <Typography variant="body2">
          Filtrados: {filteredRows.length} | Seleccionados: {selectedKeys.length}
        </Typography>
        <Button
          size="small"
          onClick={() => (headerAllChecked ? unselectAllVisible() : selectAllVisible())}
        >
          {headerAllChecked ? "Deseleccionar visibles" : "Seleccionar visibles"}
        </Button>
      </Box>

      <TableContainer component={Paper} style={{ maxHeight: 320 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={headerAllChecked}
                  indeterminate={headerIndeterminate}
                  onClick={() => (headerAllChecked ? unselectAllVisible() : selectAllVisible())}
                />
              </TableCell>
              {COLUMNAS_ORDENABLES.map((col) => (
                <TableCell key={col.key}>
                  <TableSortLabel
                    active={ordenarPor === col.key}
                    direction={ordenarPor === col.key ? direccionOrden : "asc"}
                    onClick={() => ordenarPorColumna(col.key)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row) => {
              const selected = selectedSet.has(row.key);
              return (
                <TableRow
                  key={row.key}
                  hover
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleSelected(row)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox checked={selected} />
                  </TableCell>
                  <TableCell>{row.numero}</TableCell>
                  <TableCell>
                    {row.nombres} {row.apellidos}
                  </TableCell>
                  <TableCell>{row.__modalidadLabel}</TableCell>
                  <TableCell>{row.__categoriaLabel}</TableCell>
                  <TableCell>{row.__ramaLabel}</TableCell>
                </TableRow>
              );
            })}
            {filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2">No hay inscritos con los filtros actuales.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
